// src/admin/components/AdminAIChat.tsx
//
// Floating AI assistant widget, mounted once in AdminLayout so it's
// available on every admin page. Talks to the `adminChat` Cloud Function
// (Gemini-powered) — see new/functions/functions/functions/src/ai.ts.
//
// Guardrails (auth questions refused, finance questions allowed, staff-only
// access) are enforced server-side in the Cloud Function, not here — this
// component is just the UI.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Avatar,
  Tooltip,
  Zoom,
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

interface ChatTurn {
  role: 'user' | 'model';
  text: string;
}

interface AdminChatResponse {
  reply: string;
}

// Gemini replies sometimes include lightweight markdown (**bold**, "- " bullet
// lists) even when told not to — render it instead of showing literal `**`.
const parseInline = (line: string, keyPrefix: string): React.ReactNode[] =>
  line
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((part) => part.length > 0)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
      )
    );

const renderFormattedText = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let listBuffer: React.ReactNode[] = [];

  const flushList = (key: string) => {
    if (listBuffer.length > 0) {
      nodes.push(
        <Box key={key} component="ul" sx={{ m: 0, my: 0.5, pl: 2.5 }}>
          {listBuffer}
        </Box>
      );
      listBuffer = [];
    }
  };

  lines.forEach((line, idx) => {
    const bulletMatch = /^\s*[-*]\s+(.*)/.exec(line);
    if (bulletMatch) {
      listBuffer.push(<li key={`li-${idx}`}>{parseInline(bulletMatch[1], `li-${idx}`)}</li>);
      return;
    }
    flushList(`ul-${idx}`);
    nodes.push(
      <React.Fragment key={`line-${idx}`}>
        {parseInline(line, `line-${idx}`)}
        {idx < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
  flushList('ul-end');

  return nodes;
};

const adminChatCallable = httpsCallable<{ message: string; history: ChatTurn[] }, AdminChatResponse>(
  functions,
  'adminChat'
);

const AdminAIChat: React.FC = () => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open, sending]);

  if (!currentUser) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setError('');
    setInput('');
    const nextMessages: ChatTurn[] = [...messages, { role: 'user', text }];
    setMessages(nextMessages);
    setSending(true);

    try {
      const result = await adminChatCallable({ message: text, history: messages });
      setMessages([...nextMessages, { role: 'model', text: result.data.reply }]);
    } catch (err: any) {
      setError(err?.message || 'Failed to get a response. Please try again.');
      setMessages(nextMessages);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Zoom in={!open}>
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.drawer + 300 }}
          aria-label="Open AI Assistant"
        >
          <SmartToyIcon />
        </Fab>
      </Zoom>

      <Zoom in={open}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            height: { xs: 'calc(100vh - 100px)', sm: 520 },
            maxHeight: '80vh',
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: (t) => t.zIndex.drawer + 300,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
                <SmartToyIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Platform &amp; business questions
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, bgcolor: 'background.default' }}>
            {messages.length === 0 && (
              <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
                <SmartToyIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Ask me about how the platform works, or about sales, revenue, costs, orders, suppliers and more.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  I can't help with logins, passwords, or admin access.
                </Typography>
              </Box>
            )}

            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.role === 'user' ? 'primary.main' : 'background.paper',
                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    border: m.role === 'user' ? 'none' : '1px solid',
                    borderColor: 'divider',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography variant="body2" component="div">{renderFormattedText(m.text)}</Typography>
                </Box>
              </Box>
            ))}

            {sending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Thinking…</Typography>
              </Box>
            )}

            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>

          {/* Input */}
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexShrink: 0 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about orders, revenue, stock…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              multiline
              maxRows={3}
            />
            <Tooltip title="Send">
              <span>
                <IconButton color="primary" onClick={handleSend} disabled={sending || !input.trim()}>
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
};

export default AdminAIChat;
